import type { ClausulaDoContrato, RegraDoContrato } from '../api/mapeamento';

/** Uma seção do documento ("Anexo I – …"): as cláusulas que começam nela e as regras dela fora de cláusula. */
export interface SecaoDoContrato {
  readonly chave: string;
  readonly nome: string | null;
  readonly paginaInicio: number;
  readonly paginaFim: number;
  readonly clausulas: readonly ClausulaDoContrato[];
  readonly foraDeClausula: readonly RegraDoContrato[];
}

interface SecaoEmMontagem {
  chave: string;
  nome: string | null;
  paginaInicio: number;
  paginaFim: number;
  clausulas: ClausulaDoContrato[];
  foraDeClausula: RegraDoContrato[];
  ordemDaFonte: number;
}

/**
 * Agrupa pela seção que o mapa deu a cada página, por fonte (o corpo e os anexos podem ser PDFs separados), na
 * ordem do documento; a seção sem nome vai por último dentro da fonte. Achado na leitura do Bradesco RJ
 * (22/10/2026): o Anexo I inteiro (diária, pacotes, remoções) não tem cláusula numerada e caía num balde só,
 * "fora de cláusula", no fim da aba.
 */
export function agruparPorSecao(
  clausulas: readonly ClausulaDoContrato[],
  regrasForaDeClausula: readonly RegraDoContrato[],
): SecaoDoContrato[] {
  const ordemDaFonte = new Map<string, number>();
  const secoes = new Map<string, SecaoEmMontagem>();

  function secao(fonteId: string, nome: string | null, pagina: number, paginaFim: number): SecaoEmMontagem {
    if (!ordemDaFonte.has(fonteId)) ordemDaFonte.set(fonteId, ordemDaFonte.size);
    const chave = `${fonteId}|${nome ?? ''}`;
    const alvo = secoes.get(chave) ?? {
      chave,
      nome,
      paginaInicio: pagina,
      paginaFim,
      clausulas: [],
      foraDeClausula: [],
      ordemDaFonte: ordemDaFonte.get(fonteId) ?? 0,
    };
    alvo.paginaInicio = Math.min(alvo.paginaInicio, pagina);
    alvo.paginaFim = Math.max(alvo.paginaFim, paginaFim);
    secoes.set(chave, alvo);
    return alvo;
  }

  for (const clausula of clausulas) {
    secao(clausula.fonteId, clausula.secao, clausula.paginaInicio, clausula.paginaFim).clausulas.push(clausula);
  }
  for (const regra of regrasForaDeClausula) {
    secao(regra.fonteId, regra.secao, regra.pagina, regra.paginaFim ?? regra.pagina).foraDeClausula.push(regra);
  }

  return [...secoes.values()]
    .sort(
      (a, b) =>
        a.ordemDaFonte - b.ordemDaFonte || Number(a.nome === null) - Number(b.nome === null) || a.paginaInicio - b.paginaInicio,
    )
    .map((s) => ({
      chave: s.chave,
      nome: s.nome,
      paginaInicio: s.paginaInicio,
      paginaFim: s.paginaFim,
      clausulas: s.clausulas,
      foraDeClausula: s.foraDeClausula,
    }));
}
